import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Flex,
  Form,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  theme,
} from '@duxton-web/ui';
import { colorBaseNeutral75 } from '@duxton-web/design-tokens/themes/light';
import {
  CloseCircleFilled,
  DocumentOutlined,
  SearchUniversal,
  TickMarkFilled,
  WarningFilled,
} from '@duxton-web/icons';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { searchCommsTrace } from 'omega-rtc/actions/debugging-tools';
import {
  getCommsTraceTimezoneOptions,
  getTimezoneCode,
} from 'omega-rtc/utils/time-utils';
import DebuggingToolsDrawer, {
  formatDateTimeWithZone,
} from './DebuggingToolsDrawer';
import { getCampaignUrl } from './debuggingToolsConstants';
import {
  StatusTone,
  endStateLabel,
  effectiveEndState,
  effectiveStoppedAt,
  endStateTone,
  getLatestEventTime,
  journeyKey,
  stoppedAtLabel,
} from './commsTraceStatus';
import {
  normalizeUserIDs,
  toTraceRange,
  USER_ID_TOKEN_SEPARATORS,
  validateRange,
  validateUserIDs,
} from './commsTraceForm';
import type { CommsTraceJourney, CommsTraceStoppedAt } from './types';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const RESULTS_PAGE_SIZE = 25;

const missingValue = <Typography.Text type="secondary">-</Typography.Text>;

const STOPPED_AT_COLORS: Record<CommsTraceStoppedAt, string> = {
  relevance_filter: 'magenta',
  prefilter: 'purple',
  similarity_check: 'cyan',
  xco: 'lime',
  hedwig_delivery: 'gold',
};

const outcomeTagProps = (tone: StatusTone): { color: string; icon?: React.ReactNode } => {
  switch (tone) {
    case 'success':
      return { color: 'success', icon: <TickMarkFilled /> };
    case 'error':
      return { color: 'error', icon: <CloseCircleFilled /> };
    case 'warning':
      return { color: 'warning', icon: <WarningFilled /> };
    default:
      return { color: 'default' };
  }
};

const getContentUrl = (templateID: string): string =>
  `${window.Grab.config.HEDWIG_DASH_URI}/templates/create/push/?id=${templateID}`;

const firstPushComponent = (journey: CommsTraceJourney) =>
  (journey.stages?.hedwig_delivery?.components || []).find(
    c => c.channel === 'PUSH' && (c.title || c.content),
  );

const renderCampaign = (journey: CommsTraceJourney) => {
  if (!journey.campaign) return missingValue;

  const name = journey.campaign.name || journey.campaign.id || journey.tracking_id;
  if (!name) return missingValue;

  const url = getCampaignUrl(
    journey.tracking_id,
    journey.campaign.id,
    journey.campaign.country_id,
  );
  return (
    <Tooltip title={name}>
      {url ? (
        <Typography.Link href={url} target="_blank" rel="noreferrer">
          {name}
        </Typography.Link>
      ) : (
        <Typography.Text>{name}</Typography.Text>
      )}
    </Tooltip>
  );
};

const renderContent = (journey: CommsTraceJourney) => {
  const push = firstPushComponent(journey);

  if (push) {
    const text = [push.title, push.content].filter(Boolean).join('\n');
    return (
      <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{text}</span>}>
        <Typography.Paragraph
          copyable={{ text }}
          ellipsis={{ rows: 3 }}
          style={{ marginBottom: 0, whiteSpace: 'pre-line' }}
        >
          {text}
        </Typography.Paragraph>
      </Tooltip>
    );
  }

  if (!journey.template_id) return missingValue;

  return (
    <Flex vertical gap={4}>
      <Tooltip title={journey.template_id}>
        <Typography.Link
          href={getContentUrl(journey.template_id)}
          target="_blank"
          rel="noreferrer"
        >
          {journey.template_id}
        </Typography.Link>
      </Tooltip>
      {journey.campaign && (
        <Tag color="blue" icon={<DocumentOutlined />}>
          {journey.campaign.is_trident ? 'Trident' : 'SmartComms'}
        </Tag>
      )}
    </Flex>
  );
};

type KeyedJourney = CommsTraceJourney & { __key: string };

const DebuggingToolsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [selectedJourney, setSelectedJourney] = useState<CommsTraceJourney | null>(null);

  const journeys: CommsTraceJourney[] = useSelector((state: any) =>
    _.get(state, 'debuggingTools.journeys', []),
  );
  const total: number = useSelector((state: any) =>
    _.get(state, 'debuggingTools.total', 0),
  );
  const truncated: boolean = useSelector((state: any) =>
    _.get(state, 'debuggingTools.truncated', false),
  );
  const resultTimezone: string = useSelector((state: any) =>
    _.get(state, 'debuggingTools.timezone', 'UTC'),
  );
  const hasSearched: boolean = useSelector((state: any) =>
    _.get(state, 'debuggingTools.hasSearched', false),
  );
  const notConfigured: boolean = useSelector((state: any) =>
    _.get(state, 'debuggingTools.notConfigured', false),
  );
  const countries = useSelector((state: any) => _.get(state, 'countries.entities', []));
  const searchRequest = useSelector((state: any) =>
    _.get(state.requests, 'searchCommsTraceRequest', {}),
  );
  const isPending = Boolean(searchRequest.isPending);

  const timezoneOptions = useMemo(() => getCommsTraceTimezoneOptions(countries), [countries]);
  const resultTimezoneCode = getTimezoneCode(countries, resultTimezone);

  // A new search clears the previous result set, so the drawer must not keep showing one of its rows.
  useEffect(() => {
    if (isPending) setSelectedJourney(null);
  }, [isPending]);

  useEffect(() => {
    if (hasSearched && journeys.length === 0) setSelectedJourney(null);
  }, [hasSearched, journeys]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const handler = (event: ErrorEvent) => {
        if (event.message === 'ResizeObserver loop completed with undelivered notifications.') {
          event.stopImmediatePropagation();
        }
      };
      window.addEventListener('error', handler);
      return () => window.removeEventListener('error', handler);
    }
  }, []);

  const columns = [
    {
      title: 'User ID',
      key: 'user_id',
      width: 160,
      render: (_v: unknown, journey: CommsTraceJourney) => (
        <Flex align="center" gap={6}>
          <span>{journey.user_id}</span>
          {journey.user_type && journey.user_type.toLowerCase() !== 'passenger' && (
            <Tag>{journey.user_type}</Tag>
          )}
        </Flex>
      ),
    },
    {
      title: 'Latest event time',
      key: 'latest_event_time',
      width: 200,
      render: (_v: unknown, journey: CommsTraceJourney) =>
        formatDateTimeWithZone(getLatestEventTime(journey), resultTimezone, resultTimezoneCode),
    },
    {
      title: 'Outcome',
      key: 'end_state',
      width: 160,
      render: (_v: unknown, journey: CommsTraceJourney) => (
        <Tag {...outcomeTagProps(endStateTone(effectiveEndState(journey)))}>
          {endStateLabel(effectiveEndState(journey))}
        </Tag>
      ),
    },
    {
      title: 'Stopped At',
      key: 'stopped_at',
      width: 180,
      render: (_v: unknown, journey: CommsTraceJourney) => {
        const stoppedAt = effectiveStoppedAt(journey);
        if (!stoppedAt) return missingValue;
        return (
          <Tooltip title={journey.reason || undefined}>
            <Tag bordered={false} color={STOPPED_AT_COLORS[stoppedAt]}>
              {stoppedAtLabel(stoppedAt)}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Campaign',
      key: 'campaign',
      width: 360,
      ellipsis: true,
      render: (_v: unknown, journey: CommsTraceJourney) => renderCampaign(journey),
    },
    {
      title: 'Content',
      key: 'content',
      width: 320,
      ellipsis: true,
      render: (_v: unknown, journey: CommsTraceJourney) => renderContent(journey),
    },
  ];

  const onSearch = () => {
    setSelectedJourney(null);
    form
      .validateFields()
      .then((values: any) => {
        const { userIDs, dateRange, timezone } = values;
        const { startTime, endTime } = toTraceRange(dateRange, timezone);
        dispatch(
          searchCommsTrace({
            userIDs: normalizeUserIDs(userIDs),
            startTime,
            endTime,
            timezone,
          }),
        );
      })
      .catch((error: any) => {
        if (!error?.errorFields) console.error('Search failed:', error);
      });
  };

  const dataSource: KeyedJourney[] = useMemo(
    () => journeys.map((j, i) => ({ ...j, __key: journeyKey(j, i) })),
    [journeys],
  );

  const renderResults = () => {
    if (!hasSearched && !isPending) {
      return <Empty description="No search has run yet." />;
    }

    if (hasSearched && notConfigured && !isPending) {
      return <Empty description="Comms trace is not configured for this environment yet" />;
    }

    if (hasSearched && journeys.length === 0 && !isPending) {
      return <Empty description="No journeys found for these users in the selected range." />;
    }

    return (
      <Card
        style={{ borderColor: token.colorBorderSecondary }}
        styles={{ body: { padding: 0 } }}
      >
        <Spin spinning={isPending}>
          <Table
            dataSource={dataSource}
            columns={columns}
            rowKey="__key"
            scroll={{ x: 'max-content' }}
            size="small"
            pagination={{
              pageSize: RESULTS_PAGE_SIZE,
              showSizeChanger: false,
            }}
            onRow={(journey: KeyedJourney) => ({
              onClick: () => setSelectedJourney(journey),
              style: { cursor: 'pointer' },
            })}
          />
        </Spin>
      </Card>
    );
  };

  return (
    <div
      style={{
        minHeight: '100%',
        padding: token.paddingLG,
        background: token.colorBgContainer,
      }}
    >
      <Flex
        align="center"
        justify="space-between"
        style={{ marginBottom: token.marginLG }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Debugging Tools
        </Title>
        {hasSearched && !notConfigured && (
          <Flex vertical align="flex-end">
            <Typography.Metadata type="secondary">{total} journeys</Typography.Metadata>
          </Flex>
        )}
      </Flex>
      <Card
        style={{
          marginBottom: '24px',
          borderColor: colorBaseNeutral75,
          background: '#fafafa',
        }}
        styles={{ body: { padding: '16px 20px' } }}
      >
        <Form form={form} layout="inline" colon={false}>
          <Flex wrap="wrap" align="flex-start" gap={12}>
            <Form.Item
              name="userIDs"
              rules={[
                {
                  validator: (_rule: unknown, value: string[]) => {
                    const message = validateUserIDs(value);
                    return message ? Promise.reject(new Error(message)) : Promise.resolve();
                  },
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <Select
                mode="tags"
                tokenSeparators={USER_ID_TOKEN_SEPARATORS}
                placeholder="User IDs (paste up to 20)"
                open={false}
                style={{ width: '280px', minHeight: '36px' }}
              />
            </Form.Item>
            <Form.Item
              name="timezone"
              rules={[{ required: true, message: 'Timezone is required' }]}
              style={{ marginBottom: 0 }}
            >
              <Select
                options={timezoneOptions}
                placeholder="Timezone"
                style={{ width: '160px', height: '36px' }}
              />
            </Form.Item>
            <Form.Item
              name="dateRange"
              dependencies={['timezone']}
              rules={[
                {
                  validator: (_rule: unknown, value: any) => {
                    const message = validateRange(value, form.getFieldValue('timezone'));
                    return message ? Promise.reject(new Error(message)) : Promise.resolve();
                  },
                },
              ]}
              style={{ marginBottom: 0 }}
            >
              <RangePicker
                showTime
                format="YYYY-MM-DD HH:mm"
                placeholder={['Start date', 'End date']}
                style={{ height: '36px' }}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                onClick={onSearch}
                loading={isPending}
                disabled={isPending}
                icon={<SearchUniversal />}
                style={{ height: '36px' }}
              >
                Search
              </Button>
            </Form.Item>
          </Flex>
        </Form>
      </Card>
      {truncated && (
        <Alert
          type="warning"
          showIcon
          message="Results hit the 1000-event cap. The oldest events were dropped, so the oldest journeys may be missing or incomplete. Narrow the range or the user list."
          style={{ marginBottom: 16 }}
        />
      )}
      {renderResults()}
      <DebuggingToolsDrawer
        journey={selectedJourney}
        timezone={resultTimezone}
        timezoneCode={resultTimezoneCode}
        open={!!selectedJourney}
        onClose={() => setSelectedJourney(null)}
      />
    </div>
  );
};

export default DebuggingToolsPage;
